import { getDetailViewFromHash, getDetailViewPanelId } from "./detailViews.js";

export const DETAIL_VIEW_QUERY_PARAM = "detailView";
export const RESULTS_SHELL_NAVIGATION_EVENT = "results-shell:navigate";
export const RESULTS_DETAIL_VIEW_CHANGE_EVENT = "results-detail-view:change";

let resultsShellNavigationSnapshot = {
  detail: null,
  token: 0,
};

const resultsShellNavigationListeners = new Set();

function emitResultsShellNavigationChange() {
  resultsShellNavigationListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener errors to keep navigation propagation stable.
    }
  });
}

function emitWindowDetailEvent(eventName, detail) {
  if (
    typeof window === "undefined"
    || typeof window.dispatchEvent !== "function"
    || typeof CustomEvent !== "function"
  ) {
    return;
  }

  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}

export function normalizeDetailViewName(viewName = "") {
  const normalizedViewName = typeof viewName === "string" ? viewName.trim().toLowerCase() : "";
  return getDetailViewPanelId(normalizedViewName) ? normalizedViewName : null;
}

export function getDetailViewFromSearch(search = "") {
  const searchParams = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDetailViewName(searchParams.get(DETAIL_VIEW_QUERY_PARAM) || "");
}

export function clearResultsDetailViewSearchParam(searchParams) {
  if (!searchParams || typeof searchParams.delete !== "function") {
    return searchParams;
  }

  searchParams.delete(DETAIL_VIEW_QUERY_PARAM);
  return searchParams;
}

export function getDetailViewFromLocation(locationLike = null) {
  const resolvedLocation = locationLike || (typeof window !== "undefined" ? window.location : null);
  if (!resolvedLocation) {
    return null;
  }

  return getDetailViewFromSearch(resolvedLocation.search) || getDetailViewFromHash(resolvedLocation.hash);
}

export function buildDetailViewLocation(viewName, locationLike = null) {
  const resolvedLocation = locationLike || (typeof window !== "undefined" ? window.location : null);
  if (!resolvedLocation) {
    return "";
  }

  const pathname = typeof resolvedLocation.pathname === "string" ? resolvedLocation.pathname : "";
  const searchParams = new URLSearchParams(typeof resolvedLocation.search === "string" ? resolvedLocation.search : "");
  const normalizedViewName = normalizeDetailViewName(viewName);

  if (!normalizedViewName) {
    searchParams.delete(DETAIL_VIEW_QUERY_PARAM);
    const nextSearch = searchParams.toString();
    return `${pathname}${nextSearch ? `?${nextSearch}` : ""}`;
  }

  searchParams.set(DETAIL_VIEW_QUERY_PARAM, normalizedViewName);
  const nextSearch = searchParams.toString();
  const panelId = getDetailViewPanelId(normalizedViewName);
  return `${pathname}${nextSearch ? `?${nextSearch}` : ""}${panelId ? `#${panelId}` : ""}`;
}

export function syncDetailViewLocation(viewName, options = {}) {
  if (typeof window === "undefined") {
    return;
  }

  const historyMethod = options.historyMode === "push" ? "pushState" : "replaceState";
  const nextLocation = buildDetailViewLocation(viewName, options.locationLike || window.location);
  window.history[historyMethod](null, "", nextLocation);
}

export function getResultsDefaultNavKey(pathname = "") {
  if (pathname === "/results" || pathname === "/results/latest") {
    return "affectation";
  }

  return null;
}

export function resolveDispatchShellNavigationView(currentViewName = null) {
  const normalizedViewName = normalizeDetailViewName(currentViewName);
  // Route = aucune vue détail active; depuis Services ou un autre panneau, revenir à Vue flotte.
  return normalizedViewName ? "fleet" : null;
}

export function emitResultsDetailViewChange(detail = {}) {
  const normalizedDetail = detail && typeof detail === "object"
    ? {
        ...detail,
        activeView: normalizeDetailViewName(detail.activeView),
      }
    : null;

  emitWindowDetailEvent(RESULTS_DETAIL_VIEW_CHANGE_EVENT, normalizedDetail);
}

function parseShellNavigationHref(href = "") {
  if (typeof href !== "string" || !href.trim() || typeof window === "undefined") {
    return null;
  }

  try {
    return new URL(href, window.location.origin);
  } catch (error) {
    return null;
  }
}

export function shouldHandleInPlaceShellNavigation(href = "", currentPathname = "") {
  const parsedHref = parseShellNavigationHref(href);
  if (!parsedHref || typeof window === "undefined") {
    return false;
  }

  return parsedHref.origin === window.location.origin && parsedHref.pathname === currentPathname;
}

export function resolveDetailViewFromHrefOrName({ detailViewLink = "", href = "" } = {}) {
  const directViewName = normalizeDetailViewName(detailViewLink);
  if (directViewName) {
    return directViewName;
  }

  const parsedHref = parseShellNavigationHref(href);
  if (!parsedHref) {
    return null;
  }

  return getDetailViewFromSearch(parsedHref.search) || getDetailViewFromHash(parsedHref.hash);
}

export function getResultsShellNavigationSnapshot() {
  return resultsShellNavigationSnapshot;
}

export function subscribeResultsShellNavigation(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  resultsShellNavigationListeners.add(listener);
  return () => {
    resultsShellNavigationListeners.delete(listener);
  };
}

export function dispatchResultsShellNavigation(detail = {}) {
  const normalizedDetail = detail && typeof detail === "object"
    ? {
        ...detail,
        viewName: normalizeDetailViewName(detail.viewName),
      }
    : null;

  resultsShellNavigationSnapshot = {
    detail: normalizedDetail,
    token: resultsShellNavigationSnapshot.token + 1,
  };
  emitResultsShellNavigationChange();
  emitWindowDetailEvent(RESULTS_SHELL_NAVIGATION_EVENT, normalizedDetail);
}

export function consumeResultsShellNavigation(token) {
  if (resultsShellNavigationSnapshot.token !== token || !resultsShellNavigationSnapshot.detail) {
    return;
  }

  resultsShellNavigationSnapshot = {
    detail: null,
    token: resultsShellNavigationSnapshot.token,
  };
  emitResultsShellNavigationChange();
}
